import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, User as UserIcon, ArrowRight, CreditCard, Mail, CheckCircle2, Camera, ScanFace, ShieldCheck, Store, Calendar, Users as UsersIcon } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatError } from "../lib/api";
import QrScanner from "../components/QrScanner";
import ActivationLinkedBanner from "../components/ActivationLinkedBanner";
import ImageUploader from "../components/ImageUploader";
import InstallGate from "../components/InstallGate";
import BrandMark from "../components/BrandMark";
import { isPasskeySupported, authenticatePasskey } from "../lib/passkey";
import { setManifestForRole } from "../lib/manifest";
import PasswordInput from "../components/PasswordInput";
import PhoneInput from "../components/PhoneInput";

const ROLE_META = {
  customer: { label: "Customer", icon: UsersIcon, color: "text-[#A78BFA]", bg: "bg-[#7C3AED]/15", desc: "Personal wallet, payments, events & loyalty" },
  merchant: { label: "Merchant", icon: Store, color: "text-[#10B981]", bg: "bg-[#10B981]/15", desc: "Accept payments, sell cards, run a shop" },
  organizer: { label: "Event Organizer", icon: Calendar, color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/15", desc: "Create events, scan tickets, see revenue" },
  staff: { label: "Staff", icon: ShieldCheck, color: "text-[#3B82F6]", bg: "bg-[#3B82F6]/15", desc: "On the ground scanning & customer support" },
};

export default function Auth() {
  const [sp] = useSearchParams();
  const initial = sp.get("mode") === "register" ? "register" : "login";
  const [mode, setMode] = useState(initial);

  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  // register
  const [step, setStep] = useState(1); // 1 = scan card QR, 2 = profile
  const [activationQr, setActivationQr] = useState("");
  const [displaySerial, setDisplaySerial] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [roleMap, setRoleMap] = useState({}); // { customer: "ICARD-...", ... }
  const [detectedRole, setDetectedRole] = useState("customer");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [ghanaCardUrl, setGhanaCardUrl] = useState("");
  const [selfieUrl, setSelfieUrl] = useState("");

  const { login, register } = useAuth();
  const nav = useNavigate();
  const rawNext = sp.get("next") || "";
  const nextUrl = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "";
  const giftToken = sp.get("gift") || "";

  // Round 117 — gift-claim registration: the gift link IS the activation credential.
  useEffect(() => {
    if (giftToken && initial === "register") {
      setActivationQr(`GIFT:${giftToken}`);
      setDetectedRole("customer");
      setManifestForRole("customer");
      setStep(2);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/activation/info").then((r) => {
      setRoleMap(r.data.roles || {});
      // Auto-fill activation token if user scanned a role QR with phone camera
      const stashed = sessionStorage.getItem("icard_activation_token");
      if (stashed && initial === "register") {
        // Round 53h — Merchant activation QRs go to a dedicated KYC flow.
        const upperStashed = stashed.trim().toUpperCase();
        if (/^ICAARD-MERCHANT-ACT-[A-Z0-9]{6,}$/.test(upperStashed)) {
          // Keep the token stashed; the merchant-register page reads it on mount.
          nav("/register-merchant", { replace: true });
          return;
        }
        // ⭐ Permanent printable activation QR (ICAARD-CUSTOMER-ACT-XXX / ICAARD-STUDENT-ACT-XXX)
        // — these are NOT in the legacy roleMap, so detect them by pattern and
        // jump straight into the registration form. No second scan required.
        const perm = stashed.trim().toUpperCase().match(/^ICAARD-(CUSTOMER|STUDENT|REPLACEMENT)-ACT-[A-Z0-9]{6,}$/);
        if (perm) {
          const kind = perm[1].toLowerCase();
          if (kind === "replacement") {
            toast.error("Replacement QRs are used inside the app (Settings → Replace Card), not for registration.");
            sessionStorage.removeItem("icard_activation_token");
          } else {
            setActivationQr(stashed.trim().toUpperCase());
            setDetectedRole(kind);
            setManifestForRole(kind);
            setStep(2);
            sessionStorage.removeItem("icard_activation_token");
          }
        } else {
          const role = matchRole(stashed, r.data.roles || {});
          if (role) {
            setActivationQr(stashed);
            setDetectedRole(role);
            setManifestForRole(role);
            setStep(2);
            sessionStorage.removeItem("icard_activation_token");
          }
        }
      }
      // ⭐ NEW: customer/student scan a unique printed serial — there's no shared
      // activation token, so we skip step 1 and jump straight into form entry.
      const stashedCard = sessionStorage.getItem("icard_activation_card");
      if (stashedCard && initial === "register" && !stashed) {
        const upper = stashedCard.trim().toUpperCase();
        // Round 53i — Merchant printed serials (MRC / MRCH prefix) → dedicated KYC flow.
        if (upper.startsWith("MRC") || upper.startsWith("MRCH") || upper.startsWith("MERCHANT")) {
          sessionStorage.setItem("icard_activation_token", upper);   // MerchantRegister reads this
          sessionStorage.removeItem("icard_activation_card");
          nav("/register-merchant", { replace: true });
          return;
        }
        const role = upper.startsWith("STU") ? "student" : "customer";
        setActivationQr("");          // backend will resolve role from card_serial
        setDisplaySerial(upper);      // but show the serial in the green banner
        setDetectedRole(role);
        setManifestForRole(role);
        setStep(2);
      }
    }).catch(() => {});
    api.get("/settings/public").then((r) => {
      if (r.data?.public_base_url) window.__ICARD_PUBLIC_URL__ = r.data.public_base_url;
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const matchRole = (token, map) => {
    const trimmed = (token || "").trim();
    for (const [role, t] of Object.entries(map || {})) {
      if (t === trimmed && role !== "customer-legacy") return role;
    }
    // legacy customer token still matches customer
    if (map["customer-legacy"] === trimmed) return "customer";
    return null;
  };

  const faceIdLogin = async () => {
    try {
      setBusy(true);
      const data = await authenticatePasskey(api);
      localStorage.setItem("icard_token", data.token);
      toast.success(`Welcome back, ${data.user.name.split(" ")[0]}!`);
      window.location.href = "/app";
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Face ID sign-in failed");
    } finally { setBusy(false); }
  };

  const handleScan = (text) => {
    setScannerOpen(false);
    const role = matchRole(text, roleMap);
    if (!role) {
      toast.error("This QR is not a valid iCaard activation code");
      return;
    }
    if (role === "admin") {
      toast.error("Admin accounts can only be created by an existing admin from the staff portal.");
      return;
    }
    setActivationQr(text.trim());
    setDetectedRole(role);
    setManifestForRole(role);
    toast.success(`Card verified · ${ROLE_META[role]?.label || role}`);
    setStep(2);
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await login(phone.trim(), password, remember);
      toast.success("Welcome back!");
      if (res.must_change_password) nav("/app/account/change-password?forced=1");
      else nav(nextUrl || "/app", { replace: true });
    } catch (e) {
      const msg = (e?.response?.data?.detail || e?.message || "").toString();
      const lockedOut = /lock|block|too many|attempts/i.test(msg);
      if (lockedOut) {
        if (window.confirm(`${msg}\n\nWould you like to contact admin to unblock your account?`)) {
          try {
            await api.post("/auth/contact-admin", { phone: phone.trim(), reason: "Login locked out" });
            toast.success("Request sent. an admin will get back to you shortly.");
          } catch (err) { toast.error(formatError(err)); }
        }
      } else {
        toast.error(formatError(e));
      }
    }
    finally { setBusy(false); }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || (detectedRole !== "customer" && (!email.trim() || !dob))) return toast.error("Enter your name and telephone number, plus the required details for your card type");
    if (!selfieUrl) return toast.error("Upload a profile photo to finish creating your account.");
    if (!ghanaCardUrl && !["student", "customer"].includes(detectedRole)) return toast.error("Upload your ID document (image or PDF) before activating.");
    if (!password) return toast.error("Choose a password to secure your account");
    setBusy(true);
    try {
      const cardSerial = sessionStorage.getItem("icard_activation_card") || null;
      await register({
        activation_qr: activationQr,
        phone: phone.trim(), name: name.trim(), email: email.trim(),
        dob,
        // Round 49 — no more typed national_id; the Ghana-card image IS the
        // ID. Keep the field name for back-compat by passing the image URL.
        national_id: "",
        ghana_card_url: ghanaCardUrl,
        selfie_url: selfieUrl,
        password, remember_me: remember,
        card_serial: cardSerial,
      });
      sessionStorage.removeItem("icard_activation_card");
      toast.success("iCaard activated · Wallet created");
      nav(nextUrl || "/app", { replace: true });
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const roleInfo = ROLE_META[detectedRole] || ROLE_META.customer;
  const RoleIcon = roleInfo.icon;

  return (
    <div className={`icaard-auth ${mode === "login" ? "icaard-auth-login" : ""} min-h-screen flex flex-col`}>
      <header className="border-b border-[#26263A] pt-safe">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="auth-back-home">
            <BrandMark size={96} />
          </Link>
        </div>
      </header>

      <main className="icaard-auth-main flex-1 flex items-center justify-center px-4 py-10">
        <aside className="icaard-auth-story" aria-label="About iCaard">
          <span className="icaard-eyebrow">ONE CARD. EVERYDAY POSSIBILITIES.</span>
          <h2>Your day,<br />connected.</h2>
          <p>Your services, tickets and account.<br />Together in one simple place.</p>
          <div className="icaard-auth-card-art" aria-hidden="true"><span>iCaard</span><span>ONE CARD · ONE SYSTEM · UNLIMITED ACCESS</span></div>
          <div className="icaard-auth-story-footer">Designed for life in Ghana.</div>
        </aside>
        <div className="icaard-auth-form w-full max-w-md fade-in">
          {mode === "login" && (
            <>
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#9CA3D4]">Sign in</div>
              <h1 className="font-display font-black text-3xl sm:text-4xl mt-2">Welcome back.</h1>
              <p className="text-[#9CA3D4] mt-2 text-sm">Enter your phone number, or the full name you used for quick ticket signup.</p>

              <form onSubmit={submitLogin} className="mt-7 space-y-4">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3D4]">Phone number or full name</label>
                  <input data-testid="auth-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number or full name" autoComplete="username" required className="mt-1.5 w-full rounded-xl border border-[#394052] bg-[#11131d] px-4 py-3 text-white" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3D4]">Password</label>
                  <PasswordInput testId="auth-password-input" value={password} onChange={setPassword} placeholder="Enter your password" autoComplete="current-password" className="mt-1.5" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 text-[#9CA3D4]">
                    <input data-testid="auth-remember-me" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> Remember me
                  </label>
                  <Link data-testid="auth-forgot-link" to="/auth/reset?type=password" className="text-[#A78BFA] hover:underline font-semibold">Forgot password?</Link>
                </div>
                <button data-testid="auth-submit-btn" type="submit" disabled={busy} className="w-full py-3.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 lift">
                  {busy ? "Please wait…" : "Sign in"} <ArrowRight size={16} />
                </button>
              </form>

              {isPasskeySupported() && (
                <>
                  <div className="my-5 flex items-center gap-3 text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3D4]">
                    <div className="flex-1 h-px bg-[#26263A]" /> or <div className="flex-1 h-px bg-[#26263A]" />
                  </div>
                  <button data-testid="faceid-login-btn" type="button" disabled={busy} onClick={faceIdLogin} className="w-full py-3.5 surface border-[#7C3AED]/40 hover:bg-[#13131E] text-white font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50">
                    <ScanFace size={18} className="text-[#A78BFA]" /> Sign in with a passkey
                  </button>
                </>
              )}

              <div className="mt-6 rounded-2xl border border-[#2E8B68] bg-[#113D31] p-4 text-left shadow-lg shadow-black/20 sm:p-5">
                <div className="text-base font-extrabold text-white sm:text-lg">New to iCaard?</div>
                <p className="mt-1 text-sm leading-relaxed text-[#D5E8DE]">Create an account to keep your tickets and services together.</p>
                <Link to={`/join${nextUrl ? `?next=${encodeURIComponent(nextUrl)}` : ""}`} className="mt-3 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#16A875] px-4 py-3 text-base font-extrabold text-white shadow-md transition hover:bg-[#138B62] focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-[#F4C45E]">
                  Create my account <ArrowRight size={19} aria-hidden="true" />
                </Link>
              </div>
              <div className="mt-3 text-center text-sm text-[#D5D9EA]">
                <div>Have a physical card? <Link to={`/join${nextUrl ? `?next=${encodeURIComponent(nextUrl)}` : ""}`} className="font-bold text-[#86E0B6] underline decoration-2 underline-offset-2">Scan and link it</Link></div>
                <button data-testid="auth-switch-register" onClick={() => { setMode("register"); setStep(1); }} className="mt-2 text-xs underline">Activate another role card</button>
              </div>
            </>
          )}

          {mode === "register" && <InstallGate />}
          {mode === "register" && step === 1 && (
            <>
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#9CA3D4]">Activate your iCaard</div>
              <h1 className="font-display font-black text-3xl sm:text-4xl mt-2">Scan your card</h1>
              <p className="text-[#9CA3D4] mt-2 text-sm">Point your phone camera at the QR code on the back of your physical iCaard. We'll automatically detect whether it's a customer, merchant, organizer or staff card.</p>

              {!scannerOpen ? (
                <div className="mt-7 space-y-3">
                  <button data-testid="register-open-camera-btn" onClick={() => setScannerOpen(true)} className="w-full py-4 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-xl flex items-center justify-center gap-2 lift">
                    <Camera size={18} /> Open camera & scan
                  </button>
                  <div className="bg-[#13131E] border border-[#26263A] rounded-xl p-4 text-xs text-[#9CA3D4]">
                    <div className="font-bold text-white mb-1">Don't have an iCaard yet?</div>
                    Physical iCaards are distributed at partner outlets and agents. You must have a physical card to create an account.
                  </div>
                </div>
              ) : (
                <div className="mt-6 space-y-3">
                  <QrScanner onScan={handleScan} onError={(e) => { toast.error(e); setScannerOpen(false); }} />
              <button data-testid="register-cancel-scan-btn" onClick={() => setScannerOpen(false)} className="w-full py-3 border border-[#26263A] hover:bg-[#1A1A27] font-semibold rounded-xl text-sm">Done</button>
                </div>
              )}

              <div className="mt-6 text-center text-sm text-[#9CA3D4]">
                Already activated? <button data-testid="auth-switch-login" onClick={() => setMode("login")} className="font-semibold text-white hover:text-[#7C3AED]">Sign in</button>
              </div>
            </>
          )}

          {mode === "register" && step === 2 && (
            <>
              <div className="flex items-center gap-2 text-[#7C3AED]"><CheckCircle2 size={16} /><div className="text-[10px] tracking-[0.3em] uppercase font-bold">Card verified</div></div>
              <h1 className="font-display font-black text-3xl mt-2">Complete your profile</h1>
              <p className="text-[#9CA3D4] mt-2 text-sm">A unique digital card number will be generated for you.</p>

              <ActivationLinkedBanner serial={activationQr || displaySerial} testId="auth-activation-banner" />

              {/* Detected role badge */}
              <div data-testid="detected-role-badge" className={`mt-5 flex items-center gap-3 surface rounded-xl px-3 py-3`}>
                <div className={`w-10 h-10 rounded-lg ${roleInfo.bg} ${roleInfo.color} flex items-center justify-center`}>
                  <RoleIcon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3D4]">Card type</div>
                  <div className="font-display font-bold text-white">{roleInfo.label}</div>
                </div>
                <div className="text-[10px] text-[#9CA3D4] text-right hidden sm:block max-w-[160px]">{roleInfo.desc}</div>
              </div>

              <form onSubmit={submitRegister} className="mt-5 space-y-3">
                <Field label="Full Name *" icon={UserIcon}>
                  <input data-testid="auth-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Abrantie Kofi Asante" className="w-full pl-9 pr-3 py-3 bg-white text-black border border-[#26263A] rounded-xl focus:ring-2 focus:ring-[#7C3AED] outline-none placeholder:text-gray-400" />
                </Field>
                <Field label="Phone Number *">
                  <PhoneInput testId="auth-phone-input" value={phone} onChange={setPhone} required />
                </Field>
                                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3D4]">Profile photo *</label>
                  <div className="mt-1.5">
                    <ImageUploader
                      kind="profile_photo"
                      value={selfieUrl}
                      onUploaded={setSelfieUrl}
                      testId="auth-selfie-uploader"
                    />
                  </div>
                  <div className="text-[10px] text-[#9CA3D4] mt-1">Use a clear photo. You can change your profile photo later.</div>
                </div>
                {detectedRole !== "customer" && <>
                <Field label="Email *" icon={Mail}>
                  <input data-testid="auth-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full pl-9 pr-3 py-3 bg-white text-black border border-[#26263A] rounded-xl focus:ring-2 focus:ring-[#7C3AED] outline-none placeholder:text-gray-400" />
                </Field>
                <Field label="Date of Birth *">
                  <input data-testid="auth-dob-input" required type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="w-full px-3 py-3 bg-white text-black border border-[#26263A] rounded-xl focus:ring-2 focus:ring-[#7C3AED] outline-none" />
                </Field>


                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3D4]">ID document (Ghana Card, Passport, Voter ID…) {detectedRole !== "student" && "*"}</label>
                  <div className="mt-1.5">
                    <ImageUploader
                      kind="ghana_card"
                      value={ghanaCardUrl}
                      onUploaded={setGhanaCardUrl}
                      testId="auth-ghana-card-uploader"
                      allowPdf
                    />
                  </div>
                  <div className="text-[10px] text-[#9CA3D4] mt-1">Upload a clear photo or PDF of any valid ID. {detectedRole === "student" ? "Optional for students." : "Required — no typed IDs accepted."}</div>
                </div>

                </>}
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3D4]">Password *</label>
                  <PasswordInput testId="auth-register-password" value={password} onChange={setPassword} required placeholder="Abrantie@1" autoComplete="new-password" rules className="mt-1.5" />
                </div>

                <label className="flex items-center gap-2 text-sm text-[#9CA3D4]">
                  <input data-testid="auth-register-remember" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> Remember me for 30 days
                </label>

                <button data-testid="auth-submit-btn" type="submit" disabled={busy} className="w-full py-3.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 lift">
                  <CreditCard size={16} /> {busy ? "Activating…" : "Activate iCaard"}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3D4]">{label}</label>
      <div className="relative mt-1.5">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3D4] z-10" size={16} />}
        {children}
      </div>
    </div>
  );
}
